import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
  fetchPrintReceiptDataThunk,
  clearPrintReceipt,
} from "../../../../store/slices/receiptsSlice";
import "../../../../css/receipt-print.css";

const formatDMY = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const mysql = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mysql) return `${mysql[3]}-${mysql[2]}-${mysql[1]}`;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date).replace(/\//g, "-");
};

const formatPrintedOn = (value) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).formatToParts(value);
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("day")}-${get("month")}-${get("year")}, ${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")} IST`;
};

const money = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number.toFixed(2) : "0.00";
};

const clean = (v) => String(v ?? "").trim();

// Header name by receipt type:
//   water      -> Panchayath name
//   slrm       -> SLRM Panchayath name
//   water_slrm -> Panchayath name, with SLRM name below if different
// Uses the backend value when available, so older backends still work.
const getReceiptNames = (settings, rctType) => {
  if (clean(settings.receipt_name)) {
    return {
      main: clean(settings.receipt_name),
      sub: clean(settings.receipt_sub_name),
    };
  }

  const panName = clean(settings.pan_name) || clean(settings.title) || "Gram Panchayath";
  const slrmName = clean(settings.slrm_pan_name) || clean(settings.title) || panName;
  const type = clean(rctType).toLowerCase();

  if (type === "slrm") return { main: slrmName, sub: "" };
  if (type === "water_slrm") return { main: panName, sub: slrmName !== panName ? slrmName : "" };
  return { main: panName, sub: "" };
};

const typeLabel = (rctType) => {
  const type = clean(rctType).toLowerCase();
  if (type === "slrm") return "SLRM";
  if (type === "water_slrm") return "Water + SLRM";
  if (type === "water") return "Water";
  return clean(rctType) || "-";
};

export default function PrintReceiptPage() {
  const { rct_no } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  const downloadingRef = useRef(false);
  const printingRef = useRef(false);

  const [printedOn, setPrintedOn] = useState(() => new Date());
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [actionError, setActionError] = useState("");

  const loading = useSelector((s) => s.receipts.printLoading);
  const error = useSelector((s) => s.receipts.printError);
  const data = useSelector((s) => s.receipts.printData);

  useEffect(() => {
    dispatch(clearPrintReceipt());
    if (rct_no) dispatch(fetchPrintReceiptDataThunk(rct_no));
    return () => dispatch(clearPrintReceipt());
  }, [rct_no, dispatch]);

  const stampNow = () => flushSync(() => setPrintedOn(new Date()));

  // Print opens the browser's print dialog. It does not download a PDF.
  const handlePrint = async () => {
    if (printingRef.current || downloadingRef.current) return;
    printingRef.current = true;
    setPrinting(true);
    setActionError("");

    try {
      stampNow();
      if (document.fonts?.ready) await document.fonts.ready;
      window.print();
    } catch (err) {
      console.error("Receipt print failed:", err);
      setActionError("Unable to open the print dialog. Please try again.");
    } finally {
      printingRef.current = false;
      setPrinting(false);
    }
  };

  // Separate application-generated PDF download.
  const downloadReceiptPdf = async () => {
    if (!receiptRef.current || downloadingRef.current || printingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    setActionError("");

    try {
      stampNow();
      if (document.fonts?.ready) await document.fonts.ready;

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        onclone: (clonedDocument) => {
          const sheet = clonedDocument.querySelector(".rpt-sheet");
          if (sheet) {
            sheet.style.minHeight = "0";
            sheet.style.height = "auto";
            sheet.style.maxHeight = "none";
            sheet.style.boxShadow = "none";
            sheet.style.overflow = "visible";
          }
        },
      });

      if (!canvas.width || !canvas.height) {
        throw new Error("The receipt could not be captured");
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a5",
        compress: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 6;
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;
      const imageHeight = (canvas.height * contentWidth) / canvas.width;
      const imageData = canvas.toDataURL("image/jpeg", 0.92);

      if (imageHeight <= contentHeight) {
        pdf.addImage(imageData, "JPEG", margin, margin, contentWidth, imageHeight);
      } else {
        // Long receipts span multiple A5 pages without compressing their text.
        let heightLeft = imageHeight;
        let y = margin;
        pdf.addImage(imageData, "JPEG", margin, y, contentWidth, imageHeight);
        heightLeft -= contentHeight;
        while (heightLeft > 0.5) {
          pdf.addPage();
          y = margin - (imageHeight - heightLeft);
          pdf.addImage(imageData, "JPEG", margin, y, contentWidth, imageHeight);
          heightLeft -= contentHeight;
        }
      }

      pdf.save(`receipt_${rct_no}.pdf`);
    } catch (err) {
      console.error("PDF download failed:", err);
      setActionError(`PDF download failed: ${err?.message || "Unknown error"}`);
    } finally {
      downloadingRef.current = false;
      setDownloading(false);
    }
  };

  if (loading) {
    return <div className="rpt-state-wrap"><div className="rpt-state-card">Loading receipt...</div></div>;
  }

  if (error) {
    return (
      <div className="rpt-state-wrap">
        <div className="rpt-state-card">
          <div className="rpt-error-text">{String(error)}</div>
          <button type="button" className="rpt-btn rpt-btn-dark" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="rpt-state-wrap"><div className="rpt-state-card">No Data</div></div>;
  }

  const payload = data?.data || data || {};
  const receipt = payload.receipt || {};
  const user = payload.user || {};
  const settings = payload.settings || {};
  const waterBalance = Number(receipt.water_balance ?? 0);
  const slrmBalance = Number(receipt.slrm_balance ?? receipt.slrm_bal ?? 0);
  const totalBalance = Number(receipt.total_balance ?? waterBalance + slrmBalance);
  const names = getReceiptNames(settings, receipt.rct_type);
  const rctType = clean(receipt.rct_type).toLowerCase();
  const toward =
    rctType === "slrm"
      ? "SLRM Fee"
      : rctType === "water_slrm"
      ? "Water Charges and SLRM Fee"
      : "Water Charges";

  return (
    <div className="rpt-page">
      <div className="rpt-topbar no-print">
        <button type="button" className="rpt-btn rpt-btn-light" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="rpt-topbar-actions">
          <button type="button" className="rpt-btn rpt-btn-dark" onClick={handlePrint} disabled={printing || downloading}>
            {printing ? "Opening Print..." : "Print"}
          </button>
          <button type="button" className="rpt-btn rpt-btn-primary" onClick={downloadReceiptPdf} disabled={printing || downloading}>
            {downloading ? "Generating PDF..." : "Download PDF"}
          </button>
        </div>
      </div>

      {actionError && (
        <div className="rpt-error-text no-print" role="alert" style={{ maxWidth: 820, margin: "0 auto 12px" }}>
          {actionError}
        </div>
      )}

      <div className="rpt-sheet-wrap">
        <article className="rpt-sheet" ref={receiptRef} aria-label="Payment receipt">
          <header className="rpt-header">
            <div className="rpt-office">{names.main}</div>
            {names.sub && (
              <div className="rpt-office-sub" style={{ fontWeight: 600 }}>{names.sub}</div>
            )}
            <div className="rpt-office-sub">
              {settings.address || ""}{settings.phone ? ` | Phone: ${settings.phone}` : ""}
            </div>
            <div className="rpt-receipt-title">PAYMENT RECEIPT</div>
          </header>

          <div className="rpt-meta-table">
            <div className="rpt-meta-row">
              <div className="label">Receipt No</div>
              <div className="value rpt-number">{receipt.rct_no || "-"}</div>
              <div className="label">Receipt Date</div>
              <div className="value">{formatDMY(receipt.rct_date)}</div>
            </div>
            <div className="rpt-meta-row">
              <div className="label">Payment Mode</div>
              <div className="value">{receipt.mode || "-"}</div>
              <div className="label">Receipt Type</div>
              <div className="value">{typeLabel(receipt.rct_type)}</div>
            </div>
          </div>

          <div className="rpt-section-title">House &amp; Payment Details</div>
          <table className="rpt-main-table">
            <tbody>
              <tr><td className="k">Name</td><td className="v rpt-name">{user.family_head || "-"}</td></tr>
              <tr><td className="k">Door No</td><td className="v">{user.door_no || "-"}</td></tr>
              <tr><td className="k">House Code</td><td className="v">{user.qr_code || "-"}</td></tr>
              <tr><td className="k">Toward</td><td className="v">Payment for {toward}</td></tr>
              <tr><td className="k">Water Charges</td><td className="v rpt-amount">Rs. {money(receipt.water_fee)}</td></tr>
              <tr><td className="k">SLRM Fee</td><td className="v rpt-amount">Rs. {money(receipt.slrm_fee)}</td></tr>
              <tr className="rpt-total-row"><td className="k">Total Amount Paid</td><td className="v rpt-amount rpt-strong">Rs. {money(receipt.amount)}</td></tr>
              <tr><td className="k">Water Balance</td><td className="v rpt-amount">Rs. {money(waterBalance)}</td></tr>
              <tr><td className="k">SLRM Balance</td><td className="v rpt-amount">Rs. {money(slrmBalance)}</td></tr>
              <tr><td className="k">Total Balance</td><td className="v rpt-amount rpt-strong">Rs. {money(totalBalance)}</td></tr>
              <tr><td className="k">Remarks</td><td className="v">{receipt.remarks || "-"}</td></tr>
            </tbody>
          </table>

          <footer className="rpt-footer">
            <div className="rpt-printed-on">Printed On: <strong>{formatPrintedOn(printedOn)}</strong></div>
            <div className="rpt-sign-box">
              <div className="rpt-sign-line" />
              <div className="rpt-sign-text">Authorized Signature</div>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}
